import { Table, Input, Select, DatePicker } from "antd";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import useAdminDataStore, { selectCurrentGroupData } from "../../../store/adminData/adminData";
import { fbGetDonationDataForGroup } from "../../../services/firebaseService/endPoints/admin/partners";
import { SearchOutlined } from "@ant-design/icons";
import useCurrency from "../../../utils/hooks/useCurrency";
import logger from "../../../utils/logger";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const DonationReport = () => {
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    partner: null,
    dateRange: null,
  });
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const groupCurrency = useCurrency();
  const groupData = useAdminDataStore(selectCurrentGroupData);

  const toMoment = (value) => {
    if (!value && value !== 0) return null;
    // treat >= 1e12 as ms, otherwise seconds
    return value >= 1e12 ? moment(value) : moment.unix(value);
  };

  const getTimestampValue = (record) =>
    record?.timestamp ?? record?.date ?? null;

  const fetchData = async () => {
    if (!groupData?.id) return;
    try {
      setLoading(true);

      const data = await fbGetDonationDataForGroup(groupData?.id);

      const sortedData = data.sort(
        (a, b) => (getTimestampValue(b) || 0) - (getTimestampValue(a) || 0)
      );
      setFilteredTableData(sortedData);
      setAllData(sortedData);
      const partnerMap = new Map();
      sortedData.forEach((item) => {
        if (item.partnerId && item.partnerName) {
          partnerMap.set(item.partnerId, {
            value: item.partnerId,
            label: item.partnerName,
          });
        }
      });
      setPartnerOptions(Array.from(partnerMap.values()));
      setLoading(false);
    } catch (error) {
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = React.useCallback((sourceData, nextFilters) => {
    const searchValue = (nextFilters.search || "").toLowerCase();
    const [startDate, endDate] = nextFilters.dateRange || [];
    const start = startDate ? startDate.clone().startOf("day") : null;
    const end = endDate ? endDate.clone().endOf("day") : null;

    return sourceData.filter((record) => {
      const matchesSearch = searchValue
        ? record.userName?.toLowerCase().includes(searchValue) ||
          record.partnerName?.toLowerCase().includes(searchValue) ||
          record.userEmail?.toLowerCase().includes(searchValue)
        : true;
      const matchesPartner = nextFilters.partner
        ? record.partnerId === nextFilters.partner
        : true;
      const matchesDate =
        start && end
          ? dayjs(record.date).isBetween(start, end, undefined, "[]")
          : true;
      return matchesSearch && matchesPartner && matchesDate;
    });
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: key === "partner" ? value || null : value,
    }));
  };

  useEffect(() => {
    if (groupData?.id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  const totalAmount = useMemo(
    () =>
      filteredTableData.reduce(
        (acc, val) => acc + (Number(val.amount) || 0),
        0
      ),
    [filteredTableData]
  );

  useEffect(() => {
    const filteredData = applyFilters(allData, filters);
    setFilteredTableData(filteredData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const columns = useMemo(
    () => [
      {
        title: "Name",
        className: "header-white",
        key: "userName",
        dataIndex: "userName",
      },
      {
        title: `Amount (${groupCurrency}${totalAmount.toFixed(2)})`,
        className: "header-white",
        key: "amount",
        dataIndex: "amount",
        render: (data) =>
          data ? `${groupCurrency}${parseFloat(data).toFixed(2)}` : "-",
      },
      {
        title: "Partner",
        className: "header-white",
        key: "partnerName",
        dataIndex: "partnerName",
      },
      {
        title: "Source",
        className: "header-white",
        key: "source",
        dataIndex: "source",
      },
      {
        title: "Date",
        className: "header-white",
        key: "timestamp",
        dataIndex: "timestamp",
        render: (_, record) => {
          const ts = getTimestampValue(record);
          const m = toMoment(ts);
          return m ? m.format("MM/DD/YYYY") : "";
        },
      },
      {
        title: "Email",
        className: "header-white",
        key: "userEmail",
        dataIndex: "userEmail",
      },
    ],
    [groupCurrency, totalAmount]
  );

  return (
    <div
      style={{
        marginTop: "var(--screen-general-margin)",
      }}
    >
      <div
        style={{
          marginTop: "var(--screen-general-margin)",
          backgroundColor: "white",
          padding: "12px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div
          className="d-flex justify-content-between"
          style={{ width: "100%" }}
        >
          <div>
            <Input
              prefix={<SearchOutlined />}
              value={filters.search || ""}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              name="search"
              style={{
                width: "250px",
              }}
              placeholder="Filter by partner, name or email"
              tabIndex="2"
            />
          </div>
          <div className="d-flex gap-2">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 220 }}
              placeholder="Filter by partner"
              value={filters.partner || undefined}
              options={partnerOptions}
              onChange={(value) => handleFilterChange("partner", value)}
            />
            <RangePicker
              allowClear
              value={filters.dateRange || null}
              onChange={(range) => handleFilterChange("dateRange", range)}
              format="MM/DD/YYYY"
            />
          </div>
        </div>
      </div>

      <div style={{ marginTop: "var(--screen-general-margin)" }}>
        <Table
          bordered
          loading={loading}
          columns={columns}
          dataSource={filteredTableData}
          pagination={{
            position: ["bottomLeft"],
            defaultPageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
          }}
        />
      </div>
    </div>
  );
};

export default DonationReport;
